import { motion } from "framer-motion";
import { ShoppingBag, Menu, X, User, LogOut, Package } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { copy } from "../data/copy";

export const Header = () => {
    const { lang, toggle, t } = useLang();
    const { count, setOpen } = useCart();
    const { user, logout, setAuthOpen, setAuthMode } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const navItems = [
        { to: "/shop", label: copy.nav.shop },
        { to: "/about", label: copy.nav.about },
        { to: "/faq", label: copy.nav.faq },
    ];

    const openLogin = () => { setAuthMode("login"); setAuthOpen(true); };
    const openSignup = () => { setAuthMode("signup"); setAuthOpen(true); };

    return (
        <>
            <motion.header
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
                    scrolled
                        ? "backdrop-blur-lg bg-[color:var(--kp-cream)]/95 shadow-[0_4px_20px_-8px_rgba(122,78,42,0.15)]"
                        : "backdrop-blur-md bg-[color:var(--kp-cream)]/80 md:bg-transparent"
                }`}
                data-testid="site-header"
            >
                <div className="max-w-[1300px] mx-auto px-3 md:px-10 h-14 md:h-20 flex items-center justify-between gap-2">
                    <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
                        <img
                            src="/img/logo-b.png"
                            alt="Bheema's Kumari Pickles"
                            className="w-9 h-9 md:w-12 md:h-12 rounded-full object-cover ring-1 ring-[color:var(--kp-turmeric)]/40"
                            data-testid="brand-logo-img"
                        />
                        <span className="flex flex-col leading-none">
                            <span className="font-display font-bold text-[11px] md:text-sm tracking-wide text-[color:var(--kp-brown)]">
                                Bheema's
                            </span>
                            <span className="font-display italic text-base md:text-2xl leading-tight text-[color:var(--kp-terracotta)] ml-2 md:ml-3">
                                Kumari Pickles
                            </span>
                        </span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-1 bg-[color:var(--kp-paper)]/70 rounded-full px-2 py-1.5 border border-[color:var(--kp-ink)]/8">
                        {navItems.map((n) => {
                            const active = location.pathname.startsWith(n.to);
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    className={`text-[13px] px-4 py-2 rounded-full font-bold transition-colors ${
                                        active
                                            ? "bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)]"
                                            : "text-[color:var(--kp-ink-soft)] hover:text-[color:var(--kp-terracotta)]"
                                    }`}
                                    data-testid={`nav-${n.to.replace("/", "")}`}
                                >
                                    {t(n.label)}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-1.5 md:gap-2">
                        <button
                            onClick={toggle}
                            className="text-[11px] md:text-[12px] font-bold flex items-center gap-1 border-2 border-[color:var(--kp-ink)]/15 rounded-full px-2.5 py-1 md:px-3 md:py-1.5 hover:border-[color:var(--kp-terracotta)] hover:text-[color:var(--kp-terracotta)] transition-colors"
                            data-testid="language-toggle"
                            aria-label="Toggle language"
                        >
                            <span className={lang === "en" ? "text-[color:var(--kp-terracotta)]" : "opacity-50"}>EN</span>
                            <span className="opacity-30">/</span>
                            <span
                                className={`font-telugu-body ${
                                    lang === "te" ? "text-[color:var(--kp-terracotta)]" : "opacity-50"
                                }`}
                            >
                                తె
                            </span>
                        </button>

                        {user ? (
                            <div className="relative">
                                <button
                                    onClick={() => setUserMenuOpen((v) => !v)}
                                    className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-[12px] font-bold border-2 border-[color:var(--kp-ink)]/15 rounded-full px-2.5 py-1 md:px-3 md:py-1.5 hover:border-[color:var(--kp-terracotta)] transition-colors"
                                    data-testid="user-menu-button"
                                >
                                    <User size={12} />
                                    <span className="hidden sm:inline max-w-[80px] truncate">{user.name?.split(" ")[0]}</span>
                                </button>
                                {userMenuOpen && (
                                    <div
                                        className="absolute right-0 top-full mt-2 w-48 bg-[color:var(--kp-paper)] rounded-2xl shadow-lg border border-[color:var(--kp-ink)]/10 z-50 overflow-hidden"
                                        data-testid="user-menu"
                                    >
                                        <div className="px-4 py-3 border-b border-[color:var(--kp-ink)]/10">
                                            <div className="font-display font-bold text-lg leading-tight">{user.name}</div>
                                            <div className="text-[11px] text-[color:var(--kp-brown)]/70">+91 {user.phone}</div>
                                        </div>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate("/orders"); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-[color:var(--kp-cream-2)]"
                                            data-testid="user-menu-orders"
                                        >
                                            <Package size={13} /> {t({ en: "My orders", te: "నా ఆర్డర్‌లు" })}
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate("/account"); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-[color:var(--kp-cream-2)]"
                                            data-testid="user-menu-account"
                                        >
                                            <User size={13} /> {t({ en: "My addresses", te: "నా చిరునామాలు" })}
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); logout(); }}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-[color:var(--kp-cream-2)] text-[color:var(--kp-terracotta)]"
                                            data-testid="user-menu-logout"
                                        >
                                            <LogOut size={13} /> {t({ en: "Log out", te: "లాగ్ అవుట్" })}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={openLogin}
                                className="hidden sm:inline-flex items-center gap-2 text-[12px] font-bold border-2 border-[color:var(--kp-ink)]/15 rounded-full px-3 py-1.5 hover:border-[color:var(--kp-terracotta)] hover:text-[color:var(--kp-terracotta)] transition-colors"
                                data-testid="header-login"
                            >
                                <User size={13} />
                                <span>{t({ en: "Log in", te: "లాగిన్" })}</span>
                            </button>
                        )}

                        <button
                            onClick={() => setOpen(true)}
                            className="relative flex items-center gap-1.5 md:gap-2 bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] text-[11px] md:text-[12px] font-bold rounded-full px-2.5 py-1.5 md:px-3 md:py-2 hover:bg-black transition-colors"
                            data-testid="cart-button"
                            aria-label="Open cart"
                        >
                            <ShoppingBag size={13} />
                            <span data-testid="cart-count">{count}</span>
                        </button>

                        <button
                            onClick={() => setMenuOpen((v) => !v)}
                            className="md:hidden text-[color:var(--kp-ink)] p-1 rounded-full border-2 border-[color:var(--kp-ink)]/15"
                            aria-label="Toggle menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {menuOpen ? <X size={16} /> : <Menu size={16} />}
                        </button>
                    </div>
                </div>
            </motion.header>

            {menuOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-40 md:hidden bg-[color:var(--kp-cream)] pt-24 px-6"
                    data-testid="mobile-menu"
                >
                    <div className="flex flex-col gap-6">
                        {navItems.map((n, i) => (
                            <motion.div
                                key={n.to}
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.05 * i }}
                            >
                                <Link
                                    to={n.to}
                                    onClick={() => setMenuOpen(false)}
                                    className="text-left font-display text-4xl font-bold text-[color:var(--kp-ink)]"
                                >
                                    {t(n.label)}
                                </Link>
                            </motion.div>
                        ))}
                        {user ? (
                            <>
                                <button
                                    onClick={() => { setMenuOpen(false); navigate("/orders"); }}
                                    className="text-left font-display italic text-4xl text-[color:var(--kp-terracotta)]"
                                    data-testid="mobile-orders"
                                >
                                    {t({ en: "My orders", te: "నా ఆర్డర్‌లు" })}
                                </button>
                                <button
                                    onClick={() => { setMenuOpen(false); navigate("/account"); }}
                                    className="text-left font-display italic text-4xl text-[color:var(--kp-terracotta)]"
                                    data-testid="mobile-account"
                                >
                                    {t({ en: "My addresses", te: "నా చిరునామాలు" })}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => { setMenuOpen(false); openSignup(); }}
                                className="text-left font-display italic text-4xl text-[color:var(--kp-terracotta)]"
                                data-testid="mobile-signup"
                            >
                                {t({ en: "Sign in", te: "సైన్ ఇన్" })}
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </>
    );
};
